import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import { ICusAccountIconProps, IMyAccountLinksData } from './cus-account-icon.props.autogenerated';

const CusAccountIcon = (props: ICusAccountIconProps<unknown>): JSX.Element => {
    const { config, context, resources } = props;

    const user = context.request.user;
    const isAuthenticated = user?.isAuthenticated ?? false;

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const toggleRef = useRef<HTMLButtonElement>(null);
    const dropdownId = `${props.id}-account-links`;

    const toggleDropdown = (): void => {
        setIsDropdownOpen(open => !open);
    };

    useEffect(() => {
        if (typeof window === 'undefined' || !isDropdownOpen) {
            return;
        }

        const handleMouseDown = (event: MouseEvent): void => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleMouseDown);
        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
        };
    }, [isDropdownOpen]);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
        if (event.key === 'Escape' && !event.defaultPrevented && isDropdownOpen) {
            event.preventDefault();
            event.stopPropagation();
            setIsDropdownOpen(false);
            toggleRef.current?.focus();
        }
    };

    if (!isAuthenticated) {
        return (
            <div className='bs-account-icon'>
                <a
                    className='bs-account-icon__signin-link'
                    href={user?.signInUrl}
                    title={resources.signInLinkText}
                    aria-label={resources.signInLinkAriaText}
                >
                    <span className='bs-account-icon__text'>{resources.signInLinkText}</span>
                </a>
            </div>
        );
    }

    return (
        <div className='bs-account-icon' ref={containerRef} onKeyDown={handleKeyDown}>
            <button
                ref={toggleRef}
                type='button'
                className='bs-account-icon__profile-button'
                title={resources.myProfileButtonAriaLabel}
                aria-label={user?.name || resources.myProfileButtonAriaLabel}
                aria-expanded={isDropdownOpen}
                aria-controls={isDropdownOpen ? dropdownId : undefined}
                onClick={toggleDropdown}
            >
                <span className='bs-account-icon__text'>{user?.name ?? resources.myProfileButtonAriaLabel}</span>
            </button>
            {isDropdownOpen && (
                <div id={dropdownId} className='bs-account-icon__dropdown'>
                    {config.myAccountLinks?.map((link: IMyAccountLinksData) => (
                        <a
                            key={link.linkUrl.destinationUrl}
                            className='bs-account-icon__link'
                            href={link.linkUrl.destinationUrl}
                            aria-label={link.ariaLabel}
                            target={link.openInNewTab ? '_blank' : undefined}
                            rel={link.openInNewTab ? 'noopener noreferrer' : undefined}
                        >
                            {link.linkText}
                        </a>
                    ))}
                    <a
                        className='bs-account-icon__signout-link'
                        href={user?.signOutUrl ?? ''}
                        title={resources.signOutLinkText}
                        aria-label={resources.signOutLinkAriaText}
                    >
                        {resources.signOutLinkText}
                    </a>
                </div>
            )}
        </div>
    );
};

export default CusAccountIcon;
